import {Injectable} from '@angular/core';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {ValueService} from '@ngmedax/value';

/**
 * Position of the offending part inside the raw expression (as typed in the editor)
 */
export interface FormulaErrorRange {
  start: number;
  end: number;
}

/**
 * Structured validation error for a formula expression
 */
export interface FormulaValidationError {
  type: 'syntax' | 'unknownVariable' | 'unknownMathFunction' | 'invalidFunctionCall' | 'quotes' | 'circular';
  detail?: string;
  /**
   * Detail code for well known syntax problems, so the ui can show a translated message
   */
  detailCode?: 'missingClosingParen' | 'extraClosingParen';
  /**
   * Where the offending token sits in the raw expression. Null when unknown
   */
  range?: FormulaErrorRange | null;
}

/**
 * Validates formula expressions in the editor the same way the app executes them.
 *
 * The app (mobile app: services/questionnaire/formula.ts) executes formulas via
 * vm.runInNewContext with the questionnaire variables object as context. This
 * service mirrors the relevant behavior without executing anything:
 *
 * - same save transformation (expression gets wrapped in parentheses)
 * - same "(if" unwrapping before execution
 * - same function call allowlist (if statements, Math functions, .match method)
 * - syntax check by compiling (new Function), never running the statement
 * - referenced variables are checked against the variables known to the questionnaire
 *
 * Additionally provides a conservative autofix for mechanical problems
 * (unbalanced parentheses, "else (x)" instead of "else x").
 */
@Injectable()
export class FormulaValidationService {
  /**
   * Variable scopes that exist at fill time as part of the formula execution context.
   * See mobile app: services/questionnaire/variable.ts (getInitialVariables)
   */
  private static readonly RUNTIME_SCOPES = ['inline', 'extern', 'formula', 'scoring', 'container', 'client', 'patient'];

  /**
   * Scopes whose variable names are fully known in the editor and can be checked strictly
   */
  private static readonly STRICT_SCOPES = ['inline', 'extern', 'scoring'];

  /**
   * Injects dependencies
   */
  public constructor(private value: ValueService) {
  }

  /**
   * Validates given formula expression. Returns null when valid.
   *
   * @param {string} rawExpression expression as currently typed in the editor (before save wrapping)
   * @param {Questionnaire} questionnaire questionnaire to resolve known variables from
   * @returns {FormulaValidationError | null}
   */
  public validate(rawExpression: string, questionnaire: Questionnaire): FormulaValidationError | null {
    const expression = (rawExpression || '').trim();

    // empty formulas are stored as "()" on save, both variants count as empty
    if (!expression || expression.match(/^\(\s*\)$/)) {
      return null;
    }

    // string literals are not allowed in formulas (existing editor rule)
    const quoteMatch = (rawExpression || '').match(/['"`]/);
    if (quoteMatch) {
      return {type: 'quotes', range: this.rangeAt(rawExpression, quoteMatch.index, 1)};
    }

    // formula variables can not reference other formula variables (existing editor rule)
    const circularMatch = (rawExpression || '').match(/formula\.[a-zA-Z_][a-zA-Z0-9_]*/);
    if (circularMatch) {
      return {type: 'circular', range: this.rangeOfToken(rawExpression, circularMatch[0])};
    }

    const stmt = this.toRuntimeStatement(expression);

    const functionCallError = this.checkFunctionCalls(stmt, rawExpression);
    if (functionCallError) {
      return functionCallError;
    }

    const syntaxError = this.checkSyntax(stmt, rawExpression);
    if (syntaxError) {
      return syntaxError;
    }

    return this.checkVariableReferences(stmt, rawExpression, questionnaire);
  }

  /**
   * Tries to mechanically fix given broken expression. Only fixes problems where the
   * intent is unambiguous: unbalanced parentheses and "else (x)" (which the app
   * rejects at runtime, only "else x" is valid). Returns the fixed expression when
   * the result validates cleanly, null otherwise.
   *
   * @param {string} rawExpression
   * @param {Questionnaire} questionnaire
   * @returns {string | null}
   */
  public autofix(rawExpression: string, questionnaire: Questionnaire): string | null {
    let expr = (rawExpression || '').trim();

    if (!this.validate(expr, questionnaire)) {
      return null;
    }

    // each pass fixes at most one problem, so allow a few passes
    for (let i = 0; i < 10; i++) {
      const error = this.validate(expr, questionnaire);

      if (!error) {
        break;
      }

      const fixed = this.applyFix(expr, error);

      if (fixed === null || fixed === expr) {
        break;
      }

      expr = fixed;
    }

    return this.validate(expr, questionnaire) ? null : expr;
  }

  /**
   * Applies a single mechanical fix for given error. Returns null when no fix is known
   *
   * @param {string} expr
   * @param {FormulaValidationError} error
   * @returns {string | null}
   */
  private applyFix(expr: string, error: FormulaValidationError): string | null {
    if (error.type === 'syntax') {
      const scan = this.scanParens(this.stripRegexLiterals(expr));

      if (scan && scan.kind === 'missing') {
        return expr + ')';
      }

      if (scan && scan.kind === 'extra') {
        return expr.slice(0, scan.index) + expr.slice(scan.index + 1);
      }
    }

    if (error.type === 'invalidFunctionCall' && error.detail === 'else') {
      const fixed = expr.replace(/\belse\s*\(\s*([^()]*?)\s*\)/, 'else $1');

      if (fixed !== expr) {
        return fixed;
      }
    }

    return null;
  }

  /**
   * Applies the same transformations the editor applies on save and the app applies
   * before execution, so we validate exactly what will run at fill time
   *
   * @param {string} expression
   * @returns {string}
   */
  private toRuntimeStatement(expression: string): string {
    let stmt = expression;

    // save transformation: expression gets wrapped in parentheses unless already wrapped
    // (see formula-variables.component.ts, renameVariable)
    !stmt.match(/^\([\s\S]*\)$/) && (stmt = `(${stmt})`);

    // app execution: leading "(if" causes the outer parentheses to be stripped
    // (see mobile app: services/questionnaire/formula.ts, execute)
    if (stmt.match(/^\(if/)) {
      stmt = stmt.replace(/^\(/, '');
      stmt = stmt.replace(/\)$/, '');
    }

    return stmt;
  }

  /**
   * Checks function calls against the same allowlist the app enforces at execution time.
   * Additionally verifies that referenced Math functions actually exist.
   *
   * @param {string} stmt
   * @param {string} rawExpression
   * @returns {FormulaValidationError | null}
   */
  private checkFunctionCalls(stmt: string, rawExpression: string): FormulaValidationError | null {
    const functionCalls = (stmt.match(/([a-zA-Z\. ]+\()/g) || []);

    for (const rawFunctionCall of functionCalls) {
      const functionCall = rawFunctionCall.replace(/^\s+/, '');

      const mathCall = functionCall.match(/^Math\.([a-zA-Z]+) ?\(/);
      if (mathCall) {
        if (typeof Math[mathCall[1]] !== 'function') {
          const token = `Math.${mathCall[1]}`;
          return {type: 'unknownMathFunction', detail: token, range: this.rangeOfToken(rawExpression, token)};
        }
        continue;
      }

      if (!functionCall.match(/^if\s?\(/)
        && !functionCall.match(/^[a-zA-Z\. ]+\.match\(/)
        && !functionCall.match(/^\(/)
      ) {
        const token = functionCall.replace(/\($/, '').trim();
        return {type: 'invalidFunctionCall', detail: token, range: this.rangeOfToken(rawExpression, token)};
      }
    }

    // "return" is not valid in the script context the app executes formulas in
    const returnMatch = stmt.match(/\breturn\b/);
    if (returnMatch) {
      return {type: 'syntax', detail: 'return', range: this.rangeOfToken(rawExpression, 'return')};
    }

    return null;
  }

  /**
   * Compiles the statement without executing it. Catches unbalanced parentheses,
   * broken if statements, invalid operators and the like. Unbalanced parentheses
   * are located via a paren scan so the ui can point at the offending character
   *
   * @param {string} stmt
   * @param {string} rawExpression
   * @returns {FormulaValidationError | null}
   */
  private checkSyntax(stmt: string, rawExpression: string): FormulaValidationError | null {
    // locate unbalanced parentheses upfront: the compiler message for these is cryptic
    const scan = this.scanParens(this.stripRegexLiterals(rawExpression.trim()));

    if (scan) {
      return {
        type: 'syntax',
        detailCode: scan.kind === 'missing' ? 'missingClosingParen' : 'extraClosingParen',
        range: this.rangeAt(rawExpression, scan.index, 1)
      };
    }

    try {
      // tslint:disable-next-line:no-unused-expression
      new Function(stmt);
    } catch (error) {
      if (error instanceof SyntaxError) {
        // a complaint about "}" stems from the internal function wrapper and means an unclosed parenthesis
        const missingParen = error.message.indexOf('}') !== -1;
        return missingParen
          ? {type: 'syntax', detailCode: 'missingClosingParen', range: null}
          : {type: 'syntax', detail: error.message, range: null};
      }

      // e.g. EvalError when a content security policy forbids compilation: skip this check
    }

    return null;
  }

  /**
   * Checks referenced variables (scope.name) against the variables known to the questionnaire.
   * Strict for questionnaire defined scopes (inline, extern, scoring). References to scopes
   * that do not exist in the formula execution context at all are reported as unknown.
   * Properties of client/patient/container are not checked (not fully known in the editor).
   *
   * @param {string} stmt
   * @param {string} rawExpression
   * @param {Questionnaire} questionnaire
   * @returns {FormulaValidationError | null}
   */
  private checkVariableReferences(
    stmt: string, rawExpression: string, questionnaire: Questionnaire
  ): FormulaValidationError | null {
    // remove regex literals inside .match(...) so their content is not mistaken for variable references
    const cleaned = this.stripRegexLiterals(stmt);
    const references = cleaned.match(/[a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
    const variables = this.value.get(questionnaire, ['meta', 'options', 'variables']) || {};

    for (const reference of references) {
      const [scope, varName] = reference.split('.');

      if (scope === 'Math' || varName === 'match') {
        continue;
      }

      if (FormulaValidationService.RUNTIME_SCOPES.indexOf(scope) === -1) {
        return {type: 'unknownVariable', detail: reference, range: this.rangeOfToken(rawExpression, reference)};
      }

      if (FormulaValidationService.STRICT_SCOPES.indexOf(scope) !== -1
        && !(variables[scope] && variables[scope][varName])
      ) {
        return {type: 'unknownVariable', detail: reference, range: this.rangeOfToken(rawExpression, reference)};
      }
    }

    return null;
  }

  /**
   * Replaces regex literals inside .match(...) calls so their content does not
   * confuse paren scanning or variable reference detection
   *
   * @param {string} stmt
   * @returns {string}
   */
  private stripRegexLiterals(stmt: string): string {
    return stmt.replace(/\.match\(\s*\/(?:\\.|[^\/\\])*\/[a-zA-Z]*\s*\)/g, '.match(0)');
  }

  /**
   * Scans for the first unbalanced parenthesis. Returns its position and whether a
   * closing paren is missing (unclosed "(") or extra (unmatched ")"). Null when balanced
   *
   * @param {string} expr
   * @returns {{kind: 'missing' | 'extra', index: number} | null}
   */
  private scanParens(expr: string): {kind: 'missing' | 'extra', index: number} | null {
    const openStack: number[] = [];

    for (let i = 0; i < expr.length; i++) {
      if (expr[i] === '(') {
        openStack.push(i);
      }

      if (expr[i] === ')') {
        if (!openStack.length) {
          return {kind: 'extra', index: i};
        }
        openStack.pop();
      }
    }

    return openStack.length ? {kind: 'missing', index: openStack[0]} : null;
  }

  /**
   * Returns the range of the first occurrence of given token in the raw expression
   *
   * @param {string} rawExpression
   * @param {string} token
   * @returns {FormulaErrorRange | null}
   */
  private rangeOfToken(rawExpression: string, token: string): FormulaErrorRange | null {
    const index = (rawExpression || '').indexOf(token);
    return index === -1 ? null : {start: index, end: index + token.length};
  }

  /**
   * Returns a range at given index with given length, when the index is valid
   *
   * @param {string} rawExpression
   * @param {number} index
   * @param {number} length
   * @returns {FormulaErrorRange | null}
   */
  private rangeAt(rawExpression: string, index: number, length: number): FormulaErrorRange | null {
    return (typeof index === 'number' && index >= 0 && index < (rawExpression || '').length)
      ? {start: index, end: index + length}
      : null;
  }
}
